// ABOUTME: Pure helpers for mapping Kratos flow UI nodes to form state and update bodies.
// ABOUTME: Framework-agnostic; FlowForm renders with these, useKratosFlow submits their output.

import type { UiNode, UiNodeInputAttributes, UiText } from "@ory/client"

export const inputAttrs = (node: UiNode): UiNodeInputAttributes | null =>
  node.attributes.node_type === "input" ? (node.attributes as UiNodeInputAttributes) : null

const isSubmit = (a: UiNodeInputAttributes): boolean => a.type === "submit" || a.type === "button"

/** An OIDC provider button ("Continue with Google/Apple"). */
export const isOidcButton = (node: UiNode): boolean => {
  const a = inputAttrs(node)
  return node.group === "oidc" && !!a && isSubmit(a)
}

/** A visible input field (not hidden, not a submit/button). */
export const isVisibleField = (node: UiNode): boolean => {
  const a = inputAttrs(node)
  return !!a && a.type !== "hidden" && !isSubmit(a)
}

/** A non-OIDC method/submit button (password "Sign in", code "Send code", …). */
export const isMethodButton = (node: UiNode): boolean => {
  const a = inputAttrs(node)
  return node.group !== "oidc" && !!a && isSubmit(a)
}

/** Seed controlled values from every non-submit input (csrf, hidden, visible). */
export const seedValues = (nodes: UiNode[]): Record<string, string> => {
  const seeded: Record<string, string> = {}
  for (const node of nodes) {
    const a = inputAttrs(node)
    if (a && !isSubmit(a)) seeded[a.name] = typeof a.value === "string" ? a.value : ""
  }
  return seeded
}

// Kratos JSON update bodies expect nested traits (`traits: { email }`), but node names are
// dotted (`traits.email`). Un-flatten dotted keys into nested objects.
const unflatten = (flat: Record<string, string>): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(flat)) {
    if (!key.includes(".")) {
      out[key] = value
      continue
    }
    const parts = key.split(".")
    const leaf = parts.pop()
    if (leaf === undefined) continue
    let cursor = out
    for (const part of parts) {
      const next = cursor[part]
      if (typeof next === "object" && next !== null) {
        cursor = next as Record<string, unknown>
      } else {
        const created: Record<string, unknown> = {}
        cursor[part] = created
        cursor = created
      }
    }
    cursor[leaf] = value
  }
  return out
}

/**
 * Whether a field travels in the body for the clicked button. Hidden fields (csrf_token, echoed
 * traits) always do. An OIDC submit carries nothing else — sending an empty `identifier` alongside
 * it could trip schema validation. Otherwise a field travels if it belongs to the clicked button's
 * own group or to the shared `default` one.
 */
const carriesInto = (
  a: UiNodeInputAttributes,
  nodeGroup: string,
  submitGroup: string,
  isOidcSubmit: boolean,
): boolean => {
  if (isSubmit(a)) return false
  if (a.type === "hidden") return true
  if (isOidcSubmit) return false
  return nodeGroup === "default" || nodeGroup === submitGroup
}

/**
 * Build the Kratos update body for a clicked submit button: csrf + hidden fields + the
 * fields belonging to the clicked button's group (or the default group), plus the button's
 * own name/value (method/provider). Mirrors Kratos's per-method sub-form model, then
 * un-flattens dotted keys into the nested JSON shape the SDK sends.
 */
export const buildFlowBody = (
  nodes: UiNode[],
  submitNode: UiNode,
  values: Record<string, string>,
): Record<string, unknown> => {
  const submitA = inputAttrs(submitNode)
  const isOidcSubmit = submitNode.group === "oidc"
  const flat: Record<string, string> = {}
  for (const node of nodes) {
    const a = inputAttrs(node)
    if (a && carriesInto(a, node.group, submitNode.group, isOidcSubmit)) {
      flat[a.name] = values[a.name] ?? (typeof a.value === "string" ? a.value : "")
    }
  }
  if (submitA) flat[submitA.name] = typeof submitA.value === "string" ? submitA.value : String(submitA.value ?? "")
  // OIDC submits (login `provider`, settings `link`/`unlink`) all use method "oidc".
  if (isOidcSubmit) flat.method = "oidc"
  return unflatten(flat)
}

type TFn = (key: string, options?: Record<string, unknown>) => string
const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

type LabelOpts = { codeSent?: boolean; variant?: "login" | "settings" } | undefined

interface LabelRule {
  match: (a: UiNodeInputAttributes) => boolean
  label: (a: UiNodeInputAttributes, t: TFn, opts: LabelOpts) => string
}

/**
 * The label ladder as data, in its original order — the first matching row wins. Fields first,
 * then buttons; a row that needs the flow variant or the code-sent flag reads it from `opts`.
 */
const LABEL_RULES: LabelRule[] = [
  {
    match: (a) => a.name === "identifier" || a.type === "email" || a.name === "traits.email",
    label: (_a, t) => t("auth.email"),
  },
  { match: (a) => a.name === "password", label: (_a, t) => t("auth.password") },
  { match: (a) => a.name === "code", label: (_a, t) => t("auth.code") },
  { match: (a) => a.name === "traits.first_name", label: (_a, t) => t("auth.firstName") },
  { match: (a) => a.name === "traits.last_name", label: (_a, t) => t("auth.lastName") },
  {
    match: (a) => a.name === "provider",
    label: (a, t) => t("auth.continueWith", { provider: capitalize(String(a.value ?? "")) }),
  },
  { match: (a) => a.name === "resend" || a.value === "resend", label: (_a, t) => t("auth.resend") },
  // recovery "Resend code" arrives as a submit named `email`
  { match: (a) => a.type === "submit" && a.name === "email", label: (_a, t) => t("auth.resend") },
  {
    // In a settings flow the password submit changes the password; in login it signs in.
    match: (a) => a.name === "method" && a.value === "password",
    label: (_a, t, opts) => (opts?.variant === "settings" ? t("auth.changePassword") : t("auth.signIn")),
  },
  { match: (a) => a.name === "method" && a.value === "profile", label: (_a, t) => t("auth.signUp") },
  {
    match: (a) => a.name === "method" && a.value === "code",
    label: (_a, t, opts) => (opts?.codeSent ? t("auth.submit") : t("auth.sendCode")),
  },
]

/**
 * Localize a node's label via our own i18n (by field name / method), so the language switcher
 * changes every label — Kratos returns its own (server-language) labels which wouldn't update.
 * Falls back to the Kratos label for anything we don't have a key for.
 */
export const nodeLabel = (
  node: UiNode,
  t: TFn,
  opts?: { codeSent?: boolean; variant?: "login" | "settings" },
): string => {
  const a = inputAttrs(node)
  const fallback = node.meta.label?.text ?? a?.name ?? ""
  if (!a) return fallback

  for (const rule of LABEL_RULES) {
    if (rule.match(a)) return rule.label(a, t, opts)
  }
  return fallback
}

/** Localize a Kratos UI message by its stable id (with context interpolation); English text fallback. */
export const kratosMessage = (m: UiText, t: TFn): string =>
  t(`auth.msg.${m.id}`, { ...((m.context as Record<string, unknown> | undefined) ?? {}), defaultValue: m.text })

/** Tailwind text color for a Kratos UI message by severity (error / success / info). */
export const messageClass = (type: UiText["type"]): string =>
  type === "error" ? "text-red-600" : type === "success" ? "text-green-600" : "text-gray-600"

/** OIDC provider id ("google" / "apple" / …) for a provider button, else null. */
export const oidcProvider = (node: UiNode): string | null => {
  const a = inputAttrs(node)
  return a && node.group === "oidc" ? String(a.value ?? "") : null
}

/** Sort order for method buttons: password before code, resend/others last. */
export const methodOrder = (node: UiNode): number => {
  const a = inputAttrs(node)
  if (a?.name === "method" && a.value === "password") return 0
  if (a?.name === "method" && a.value === "code") return 1
  if (a?.name === "resend" || a?.value === "resend") return 2
  return 3
}

/** Map a Kratos input node to an HTML input type + autocomplete hint. */
export const htmlInputType = (a: UiNodeInputAttributes): string => {
  if (a.type === "password") return "password"
  if (a.type === "email" || a.name === "identifier") return "email"
  return "text"
}

export const autoCompleteHint = (a: UiNodeInputAttributes): string | undefined => {
  if (a.name === "password") return "current-password"
  if (a.name === "code") return "one-time-code"
  if (a.type === "email" || a.name === "identifier") return "username"
  return undefined
}
